<script setup lang="ts">
import { api } from '@/api';
import { type CreateUpdateTextDto } from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from 'i18next-vue';
import { useRouter } from 'vue-router';
import TextForm from './components/TextForm.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator';

const { t } = useTranslation();

const router = useRouter();

const notificationStore = useNotificationStore();

const initialValues: Partial<CreateUpdateTextDto> = {
  name: t('Untitled'),
  isAvailable: true,
  isUseInSetupParameter: false,
  setupScope: [],
};

const { form } = useVeeForm<CreateUpdateTextDto>({
  schemaName: 'CreateUpdateTextDto',
  options: { initialValues },
  mode: 'add',
  customValidateFunctions: [
    backendValidator((createUpdateTextDto, errorHandlers) =>
      api.textsApi.validateText({ createUpdateTextDto }, errorHandlers),
    ),
  ],
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (createUpdateTextDto) => {
  const { success, result: text } = await api.textsApi.createText({ createUpdateTextDto }, [
    formBackendErrorHandler(form),
  ]);

  if (success) {
    notificationStore.success(t('EntityAdded', { name: text.name, iuid: text.iuid }));
    await router.push({
      name: routenames.texts.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :subtitle="$t('Text_one')" :title="values.name" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.texts.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <TextForm />
    </BasePage>
  </form>
</template>
